import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Button } from "components/common";
import { INPUT_CLS } from "helpers/formStyles";
import { getDimensionRules, saveDimensionRules, learnDimensionRules } from "../common/settingsApi";
import { Chip, ROW, SettingsCard, TD, TH, TONE } from "./settingsKit";

// AX keeps which dimensions each ledger account needs, but the ERP API does not expose it:
// JN11227444 was created without Cost center on 642114 and could not be posted. These rules
// are checked before a journal is created; AX's own message can be pasted to add one.
const errorOf = (e, fallback) => e?.response?.data?.error || e?.error || e?.message || fallback;
const when = (iso) => (iso ? String(iso).replace("T", " ").slice(0, 16) : "");

const DimensionRulesPanel = () => {
  const nextId = useRef(0);
  const withId = (rule) => ({ ...rule, id: `r${(nextId.current += 1)}`, dimensions: rule.dimensions || [] });

  const [rules, setRules] = useState(null);
  const [dimensions, setDimensions] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const apply = useCallback((data) => {
    setRules((data?.rules || []).map(withId));
    setDimensions(data?.dimensions || []);
    setDirty(false);
  }, []);

  const load = useCallback(async () => {
    setError("");
    try { apply((await getDimensionRules()).data); } catch (e) { setError(errorOf(e, "Could not load the dimension rules.")); }
  }, [apply]);
  useEffect(() => { load(); }, [load]);

  const edit = (id, change) => {
    setError("");
    setDirty(true);
    setRules((list) => list.map((r) => (r.id === id ? { ...r, ...change(r) } : r)));
  };
  const toggle = (id, key) => edit(id, (r) => ({
    dimensions: r.dimensions.includes(key) ? r.dimensions.filter((d) => d !== key) : [...r.dimensions, key],
  }));
  const remove = (id) => { setDirty(true); setRules((list) => list.filter((r) => r.id !== id)); };
  const add = () => { setDirty(true); setRules((list) => [...list, withId({ account: "", dimensions: [], source: "manual" })]); };

  const run = async (name, call, fallback) => {
    if (busy) return;
    setBusy(name); setError("");
    try {
      const res = await call();
      apply(res.data);
      toast.success(res.message || "Saved.");
    } catch (e) { setError(errorOf(e, fallback)); }
    finally { setBusy(null); }
  };
  const save = () => run("save", () => saveDimensionRules(
    rules.map(({ account, dimensions: dims }) => ({ account: account.trim(), dimensions: dims })),
  ), "Could not save the dimension rules.");
  const learn = () => run("learn", async () => {
    const res = await learnDimensionRules(message);
    setMessage("");
    return res;
  }, "Could not read that message.");

  const actions = (
    <Button size="sm" loading={busy === "save"} disabled={!rules || !dirty || !!busy} onClick={save}>
      {busy === "save" ? "Saving…" : "Save rules"}
    </Button>
  );

  return (
    <div className="tw-space-y-4">
      <SettingsCard title="ERP dimensions required by account"
        subtitle="Checked before a journal is created in ERP: a line on one of these accounts must have the dimensions ticked. AX keeps these rules, but the ERP API does not return them."
        actions={actions}
        footer={<span className="tw-text-[12px] tw-text-geist-fg-3">An account ending in * covers every account starting with it (642* → 642114, 642301…).</span>}>
        {error && <p role="alert" className="tw-m-0 tw-px-5 tw-py-2 tw-text-[13px] tw-text-geist-error">{error}</p>}
        {!rules && !error && <p className="tw-m-0 tw-px-5 tw-py-4 tw-text-[13px] tw-text-geist-fg-3">Loading…</p>}
        {rules && (
          <div className="tw-overflow-x-auto">
            <table className="tw-w-full tw-border-collapse" aria-label="Dimension rules">
              <thead>
                <tr className={ROW}>
                  <th className={TH}>Account</th>
                  {dimensions.map((d) => <th key={d.key} className={`${TH} tw-text-center`}>{d.label}</th>)}
                  <th className={TH}>Source</th>
                  <th className={TH} aria-label="Remove" />
                </tr>
              </thead>
              <tbody>
                {rules.length === 0 && (
                  <tr><td className={`${TD} tw-text-[13px] tw-text-geist-fg-3`} colSpan={dimensions.length + 3}>No rules — journals go to AX unchecked.</td></tr>
                )}
                {rules.map((rule, index) => {
                  const name = rule.account.trim() || `Rule ${index + 1}`;
                  return (
                    <tr key={rule.id} className={ROW}>
                      <td className={TD}>
                        <input aria-label={`Account of rule ${index + 1}`} value={rule.account} placeholder="642114 or 642*"
                          className={`${INPUT_CLS} tw-w-36 tw-font-mono`} disabled={!!busy}
                          onChange={(e) => edit(rule.id, () => ({ account: e.target.value.toUpperCase() }))} />
                      </td>
                      {dimensions.map((d) => (
                        <td key={d.key} className={`${TD} tw-text-center`}>
                          <input type="checkbox" aria-label={`${name} ${d.label}`} checked={rule.dimensions.includes(d.key)}
                            disabled={!!busy} onChange={() => toggle(rule.id, d.key)} />
                        </td>
                      ))}
                      <td className={TD}>
                        {rule.source === "ax"
                          ? <Chip tone={TONE.warn} icon="bi-lightning" title={rule.note}>Learned from AX{rule.learned_at ? ` · ${when(rule.learned_at)}` : ""}</Chip>
                          : <Chip tone={TONE.muted}>Added by hand</Chip>}
                      </td>
                      <td className={`${TD} tw-text-right`}>
                        <Button size="sm" variant="ghost" aria-label={`Remove ${name}`} disabled={!!busy} onClick={() => remove(rule.id)}>
                          <i className="bi bi-trash" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="tw-px-5 tw-py-3">
              <Button size="sm" variant="secondary" disabled={!!busy} onClick={add}><i className="bi bi-plus-lg" /> Add account</Button>
            </div>
          </div>
        )}
      </SettingsCard>

      <SettingsCard title="Add from an AX message"
        subtitle={'Paste what AX said when it refused to post, e.g. "Dimension Cost center must be specified for account 642114". Every rule in the text is added.'}>
        <div className="tw-p-5 tw-space-y-3">
          <textarea aria-label="AX message" value={message} rows={3} disabled={!!busy}
            className="tw-w-full tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-border tw-p-3 tw-text-sm"
            placeholder="Dimension Cost center must be specified for account 642114"
            onChange={(e) => setMessage(e.target.value)} />
          <Button size="sm" loading={busy === "learn"} disabled={!message.trim() || !!busy} onClick={learn}>
            {busy === "learn" ? "Reading…" : "Add from message"}
          </Button>
        </div>
      </SettingsCard>
    </div>
  );
};

export default DimensionRulesPanel;
